import { useEffect, useState } from 'react'
import Notiflix from 'notiflix'
import { Confirm } from 'notiflix/build/notiflix-confirm-aio'
import { Report } from 'notiflix/build/notiflix-report-aio'
import { useNavigate } from 'react-router-dom'
import api from '../../../helpers/apiRequester'
import { useAuthUserData } from '../../../authUserProvider'
import styles from './styles.module.scss'

interface PcpReferralClient {
    id: number
    title?: string | null
    firstname?: string | null
    lastname?: string | null
    email?: string | null
    telephone?: string | null
    dob?: string | null
    house_number?: string | null
    house_name?: string | null
    street?: string | null
    town_city?: string | null
    county?: string | null
    postcode?: string | null
    created_at?: string | null
    has_signed?: boolean
    signed_date?: string | null
    link_sent?: boolean
    last_sent_datetime?: string | null
    send_count?: number
}

interface SendResult {
    sendType: 'email' | 'sms'
    esignUrl: string
    longUrl?: string
    token?: string
    success: boolean
    message?: string
    sentTo: string
}

const formatDob = (raw?: string | null) => {
    if (!raw) return ''
    // backend often returns YYYY-MM-DD or full ISO; normalise to YYYY-MM-DD for <input type="date" />
    const isoDate = raw.length >= 10 ? raw.slice(0, 10) : raw
    const valid = /^\d{4}-\d{2}-\d{2}$/.test(isoDate)
    return valid ? isoDate : ''
}

// Compact date (or date+time) formatter for the "link sent" / "signed" labels.
const formatStatusDate = (raw?: string | null) => {
    if (!raw) return ''
    const d = new Date(raw)
    if (Number.isNaN(d.getTime())) return String(raw).slice(0, 10)
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

const isValidEmail = (value: string) => /\S+@\S+\.\S+/.test(value.trim())
const isValidDob = (value: string) => /^\d{4}-\d{2}-\d{2}$/.test(value.trim())

// Returns the latest date a client could be born to be at least 21 today (YYYY-MM-DD).
const maxDobForAge21 = () => {
    const d = new Date()
    d.setFullYear(d.getFullYear() - 21)
    return d.toISOString().slice(0, 10)
}

const isAtLeast21 = (isoDob: string) => {
    if (!isValidDob(isoDob)) return false
    const dob = new Date(`${isoDob}T00:00:00`)
    if (Number.isNaN(dob.getTime())) return false
    const today = new Date()
    let age = today.getFullYear() - dob.getFullYear()
    const monthDiff = today.getMonth() - dob.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
        age--
    }
    return age >= 21
}

export const PcpReferralEngagementView = () => {
    const navigate = useNavigate()
    const authUser = useAuthUserData()

    const [searchTerm, setSearchTerm] = useState('')
    const [isSearching, setIsSearching] = useState(false)
    const [searchResults, setSearchResults] = useState<PcpReferralClient[]>([])
    const [selectedClient, setSelectedClient] = useState<PcpReferralClient | null>(null)
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')

    const [emailDraft, setEmailDraft] = useState('')
    const [dobDraft, setDobDraft] = useState('')
    const [isSaving, setIsSaving] = useState(false)
    const [isSending, setIsSending] = useState<null | 'email' | 'sms'>(null)
    const [lastSendResult, setLastSendResult] = useState<SendResult | null>(null)

    useEffect(() => {
        if (selectedClient) {
            setEmailDraft(selectedClient.email || '')
            setDobDraft(formatDob(selectedClient.dob))
            setLastSendResult(null)
        } else {
            setEmailDraft('')
            setDobDraft('')
        }
    }, [selectedClient])

    const isDraftDirty = () => {
        if (!selectedClient) return false
        const currentEmail = (selectedClient.email || '').trim()
        const currentDob = formatDob(selectedClient.dob)
        return emailDraft.trim() !== currentEmail || dobDraft.trim() !== currentDob
    }

    const handleSearch = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        const term = searchTerm.trim()
        if (!term) {
            Notiflix.Report.failure('Search Required', 'Please enter a name, email or phone to search.', 'OK')
            return
        }

        setIsSearching(true)
        setSearchResults([])
        setSelectedClient(null)
        setLastSendResult(null)
        try {
            const response = await api({
                url: '/pcp-referral-search-clients',
                data: { searchTerm: term }
            })
            const results: PcpReferralClient[] = response.data?.results || []
            if (!results.length) {
                Notiflix.Report.info(
                    'No Clients Found',
                    `No PCP Referral clients matched "${term}". Try a different search.`,
                    'OK'
                )
            }
            setSearchResults(results)
        } catch (error: any) {
            if (error?.response?.status === 401) {
                Notiflix.Report.failure('Session Expired', 'Your session has expired. Please log in again.', 'OK')
                navigate('/login')
                return
            }
            Report.failure(
                'Search Error',
                error?.response?.data?.message || 'An error occurred during the search. Please try again.',
                'OK'
            )
        } finally {
            setIsSearching(false)
        }
    }

    const handleSelectClient = (client: PcpReferralClient) => {
        setSelectedClient(client)
    }

    const handleSaveDetails = async () => {
        if (!selectedClient) return
        const trimmedEmail = emailDraft.trim()
        const trimmedDob = dobDraft.trim()

        if (trimmedEmail && !isValidEmail(trimmedEmail)) {
            Notiflix.Report.failure('Invalid Email', 'Please enter a valid email address.', 'OK')
            return
        }
        if (trimmedDob && !isValidDob(trimmedDob)) {
            Notiflix.Report.failure('Invalid DOB', 'Date of birth must be in YYYY-MM-DD format.', 'OK')
            return
        }
        if (trimmedDob && !isAtLeast21(trimmedDob)) {
            Notiflix.Report.failure(
                'Client Too Young',
                'Date of birth must indicate the client is at least 21 years old.',
                'OK'
            )
            return
        }
        if (!trimmedEmail && !trimmedDob) {
            Notiflix.Report.failure('Nothing to Save', 'Please provide an email and/or DOB to save.', 'OK')
            return
        }

        setIsSaving(true)
        try {
            const response = await api({
                url: '/pcp-referral-update-client',
                data: {
                    client_id: selectedClient.id,
                    email: trimmedEmail || undefined,
                    dob: trimmedDob || undefined
                }
            })
            const updated: PcpReferralClient | null = response.data?.client || null
            const merged: PcpReferralClient = {
                ...selectedClient,
                email: trimmedEmail || selectedClient.email,
                dob: trimmedDob || selectedClient.dob,
                ...(updated || {})
            }
            setSelectedClient(merged)
            setSearchResults((prev) => prev.map((c) => (c.id === merged.id ? merged : c)))
            Notiflix.Report.success('Client Updated', 'Email and/or DOB saved successfully.', 'OK')
        } catch (error: any) {
            if (error?.response?.status === 401) {
                Notiflix.Report.failure('Session Expired', 'Your session has expired. Please log in again.', 'OK')
                navigate('/login')
                return
            }
            Report.failure(
                'Update Failed',
                error?.response?.data?.message || 'Could not update the client. Please try again.',
                'OK'
            )
        } finally {
            setIsSaving(false)
        }
    }

    const canSendEsign = (sendType: 'email' | 'sms') => {
        if (!selectedClient) return false
        if (isDraftDirty()) return false
        if (!selectedClient.email || !selectedClient.dob) return false
        if (sendType === 'sms' && !selectedClient.telephone) return false
        return true
    }

    const handleSendEsign = (sendType: 'email' | 'sms') => {
        if (!selectedClient) return

        if (isDraftDirty()) {
            Notiflix.Report.warning(
                'Save Changes First',
                'Please save your email/DOB changes before sending the e-sign link.',
                'OK'
            )
            return
        }
        if (!selectedClient.email || !selectedClient.dob) {
            Notiflix.Report.warning(
                'Missing Details',
                'Both email and DOB must be populated before sending an e-sign link.',
                'OK'
            )
            return
        }
        if (sendType === 'sms' && !selectedClient.telephone) {
            Notiflix.Report.warning(
                'No Telephone',
                'Cannot send SMS — this client has no telephone number on record.',
                'OK'
            )
            return
        }

        const channelLabel = sendType === 'email' ? 'email' : 'SMS'
        const target = sendType === 'email' ? selectedClient.email : selectedClient.telephone
        Confirm.show(
            `Send e-sign link via ${channelLabel}?`,
            `An e-sign link will be generated and sent to ${target}.`,
            'Send',
            'Cancel',
            () => {
                void dispatchSend(sendType)
            },
            () => {},
            { okButtonBackground: '#d48958', titleColor: '#2c3e50' }
        )
    }

    const dispatchSend = async (sendType: 'email' | 'sms') => {
        if (!selectedClient) return
        setIsSending(sendType)
        setLastSendResult(null)
        try {
            const response = await api({
                url: '/pcp-referral-send-esign-link',
                data: {
                    client_id: selectedClient.id,
                    sendType,
                    agent: authUser?.name || authUser?.email || 'unknown'
                }
            })
            const data = response.data
            const result: SendResult = {
                sendType,
                esignUrl: data?.esignUrl || '',
                longUrl: data?.longUrl,
                token: data?.token,
                success: !!data?.success,
                message: data?.message,
                sentTo: sendType === 'email' ? selectedClient.email || '' : selectedClient.telephone || ''
            }
            setLastSendResult(result)
            if (result.success) {
                Notiflix.Report.success(
                    'E-Sign Link Sent',
                    `Link delivered via ${sendType.toUpperCase()} to ${result.sentTo}.`,
                    'OK'
                )
            } else {
                Notiflix.Report.failure(
                    'Send Failed',
                    result.message || `Could not send via ${sendType.toUpperCase()}.`,
                    'OK'
                )
            }
        } catch (error: any) {
            if (error?.response?.status === 401) {
                Notiflix.Report.failure('Session Expired', 'Your session has expired. Please log in again.', 'OK')
                navigate('/login')
                return
            }
            const data = error?.response?.data
            setLastSendResult({
                sendType,
                esignUrl: data?.esignUrl || '',
                token: data?.token,
                success: false,
                message: data?.message || error?.message || 'Send failed',
                sentTo: sendType === 'email' ? selectedClient.email || '' : selectedClient.telephone || ''
            })
            Report.failure(
                'Send Failed',
                data?.message || 'An error occurred while sending the e-sign link. Please try again.',
                'OK'
            )
        } finally {
            setIsSending(null)
        }
    }

    return (
        <div className={styles.container}>

            <div className={styles.searchSection}>
                <form onSubmit={handleSearch} className={styles.searchForm}>
                    <div className={styles.inputGroup}>
                        <label htmlFor="referralSearch">Search clients</label>
                        <input
                            id="referralSearch"
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.currentTarget.value)}
                            placeholder="Name, email or phone number"
                        />
                        <small className={styles.fieldDescription}>
                            Matches against firstname, lastname, email and telephone.
                        </small>
                    </div>
                    <button type="submit" disabled={isSearching} className={styles.primaryButton}>
                        {isSearching ? 'Searching...' : 'Search Client'}
                    </button>
                </form>
            </div>

            {selectedClient && (
                <div className={styles.clientPanel}>
                    <div className={styles.clientPanelHeader}>
                        <h3>
                            Manage{' '}
                            {[selectedClient.firstname, selectedClient.lastname].filter(Boolean).join(' ') ||
                                `Client #${selectedClient.id}`}
                        </h3>
                        <div className={styles.clientPanelHeaderRight}>
                            <div className={styles.statusBadges}>
                                {selectedClient.has_signed && (
                                    <span className={`${styles.statusBadge} ${styles.statusBadgeSigned}`}>
                                        ✓ Signed
                                        {selectedClient.signed_date
                                            ? ` · ${formatStatusDate(selectedClient.signed_date)}`
                                            : ''}
                                    </span>
                                )}
                                {selectedClient.link_sent && (
                                    <span className={`${styles.statusBadge} ${styles.statusBadgeSent}`}>
                                        Link sent
                                        {selectedClient.last_sent_datetime
                                            ? ` · ${formatStatusDate(selectedClient.last_sent_datetime)}`
                                            : ''}
                                    </span>
                                )}
                            </div>
                            <span className={styles.clientPanelMeta}>Client ID: {selectedClient.id}</span>
                            <button
                                type="button"
                                onClick={() => setSelectedClient(null)}
                                className={styles.closeButton}
                                aria-label="Close client panel"
                            >
                                Close
                            </button>
                        </div>
                    </div>

                    <div className={styles.editGrid}>
                        <div className={styles.editField}>
                            <label htmlFor="emailEdit">Email</label>
                            <input
                                id="emailEdit"
                                type="email"
                                value={emailDraft}
                                onChange={(e) => setEmailDraft(e.currentTarget.value)}
                                placeholder="client@example.com"
                            />
                        </div>
                        <div className={styles.editField}>
                            <label htmlFor="dobEdit">Date of birth</label>
                            <input
                                id="dobEdit"
                                type="date"
                                value={dobDraft}
                                max={maxDobForAge21()}
                                onChange={(e) => setDobDraft(e.currentTarget.value)}
                            />
                            <small className={styles.fieldDescription}>Client must be at least 21 years old.</small>
                        </div>
                        <div className={styles.editField}>
                            <label>Telephone (read-only)</label>
                            <input type="text" value={selectedClient.telephone || ''} readOnly />
                        </div>
                    </div>

                    <div className={styles.actionRow}>
                        <button
                            onClick={handleSaveDetails}
                            disabled={isSaving || !isDraftDirty()}
                            className={styles.primaryButton}
                        >
                            {isSaving ? 'Saving...' : 'Save email & DOB'}
                        </button>
                        <button
                            onClick={() => handleSendEsign('email')}
                            disabled={!canSendEsign('email') || isSending !== null}
                            className={styles.sendButton}
                        >
                            {isSending === 'email' ? 'Sending email...' : 'Send e-sign link (Email)'}
                        </button>
                        <button
                            onClick={() => handleSendEsign('sms')}
                            disabled={!canSendEsign('sms') || isSending !== null}
                            className={styles.sendButton}
                        >
                            {isSending === 'sms' ? 'Sending SMS...' : 'Send e-sign link (SMS)'}
                        </button>
                    </div>

                    {(!selectedClient.email || !selectedClient.dob) && (
                        <p className={styles.helperText}>
                            Add the missing email and DOB above and click <strong>Save email &amp; DOB</strong> before
                            sending an e-sign link.
                        </p>
                    )}

                    {lastSendResult && (
                        <div
                            className={`${styles.sendResult} ${
                                lastSendResult.success ? styles.sendResultSuccess : styles.sendResultError
                            }`}
                        >
                            <h4>
                                {lastSendResult.success ? 'Sent' : 'Send failed'} via{' '}
                                {lastSendResult.sendType.toUpperCase()}
                            </h4>
                            <div className={styles.detailRow}>
                                <span className={styles.label}>Recipient:</span>
                                <span className={styles.value}>{lastSendResult.sentTo}</span>
                            </div>
                            {lastSendResult.esignUrl && (
                                <div className={styles.detailRow}>
                                    <span className={styles.label}>E-sign URL:</span>
                                    <span className={styles.value}>
                                        <a
                                            href={lastSendResult.esignUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={styles.link}
                                        >
                                            {lastSendResult.esignUrl}
                                        </a>
                                    </span>
                                </div>
                            )}
                            {lastSendResult.message && (
                                <div className={styles.detailRow}>
                                    <span className={styles.label}>Message:</span>
                                    <span className={styles.value}>{lastSendResult.message}</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {searchResults.length > 0 && (
                <div className={styles.resultsSection}>
                    <div className={styles.resultsHeader}>
                        <h3>Search Results ({searchResults.length})</h3>
                        <div className={styles.viewToggle} role="group" aria-label="Result view mode">
                            <button
                                type="button"
                                onClick={() => setViewMode('grid')}
                                className={`${styles.viewToggleButton} ${
                                    viewMode === 'grid' ? styles.viewToggleButtonActive : ''
                                }`}
                                aria-pressed={viewMode === 'grid'}
                            >
                                Grid
                            </button>
                            <button
                                type="button"
                                onClick={() => setViewMode('list')}
                                className={`${styles.viewToggleButton} ${
                                    viewMode === 'list' ? styles.viewToggleButtonActive : ''
                                }`}
                                aria-pressed={viewMode === 'list'}
                            >
                                List
                            </button>
                        </div>
                    </div>

                    <div
                        className={
                            viewMode === 'grid' ? styles.resultsGrid : styles.resultsList
                        }
                    >
                        {searchResults.map((client) => {
                            const isSelected = selectedClient?.id === client.id
                            const isListMode = viewMode === 'list'
                            return (
                                <div
                                    key={client.id}
                                    className={`${
                                        isListMode ? styles.resultRow : styles.resultCard
                                    } ${isSelected ? styles.resultCardActive : ''}`}
                                >
                                    <div className={styles.resultHeader}>
                                        <h4>
                                            {[client.firstname, client.lastname].filter(Boolean).join(' ') ||
                                                'Unnamed client'}
                                        </h4>
                                        <span className={styles.resultId}>#{client.id}</span>
                                    </div>
                                    <div className={styles.statusBadges}>
                                        {client.has_signed ? (
                                            <span
                                                className={`${styles.statusBadge} ${styles.statusBadgeSigned}`}
                                                title={
                                                    client.signed_date
                                                        ? `Signed ${formatStatusDate(client.signed_date)}`
                                                        : 'CFA signed'
                                                }
                                            >
                                                ✓ Signed
                                                {client.signed_date
                                                    ? ` · ${formatStatusDate(client.signed_date)}`
                                                    : ''}
                                            </span>
                                        ) : (
                                            <span
                                                className={`${styles.statusBadge} ${styles.statusBadgeUnsigned}`}
                                            >
                                                Not signed
                                            </span>
                                        )}
                                        {client.link_sent ? (
                                            <span
                                                className={`${styles.statusBadge} ${styles.statusBadgeSent}`}
                                                title={
                                                    client.last_sent_datetime
                                                        ? `Last sent ${formatStatusDate(
                                                              client.last_sent_datetime
                                                          )}${
                                                              client.send_count
                                                                  ? ` (${client.send_count} send${
                                                                        client.send_count > 1 ? 's' : ''
                                                                    })`
                                                                  : ''
                                                          }`
                                                        : 'Link sent'
                                                }
                                            >
                                                Link sent
                                                {client.last_sent_datetime
                                                    ? ` · ${formatStatusDate(client.last_sent_datetime)}`
                                                    : ''}
                                            </span>
                                        ) : (
                                            <span
                                                className={`${styles.statusBadge} ${styles.statusBadgeNotSent}`}
                                            >
                                                No link sent
                                            </span>
                                        )}
                                    </div>
                                    <div
                                        className={
                                            isListMode ? styles.resultDetailsList : styles.resultDetails
                                        }
                                    >
                                        <div className={styles.detailRow}>
                                            <span className={styles.label}>Email:</span>
                                            <span className={styles.value}>{client.email || '—'}</span>
                                        </div>
                                        <div className={styles.detailRow}>
                                            <span className={styles.label}>Phone:</span>
                                            <span className={styles.value}>{client.telephone || '—'}</span>
                                        </div>
                                        <div className={styles.detailRow}>
                                            <span className={styles.label}>DOB:</span>
                                            <span className={styles.value}>{formatDob(client.dob) || '—'}</span>
                                        </div>
                                        <div className={styles.detailRow}>
                                            <span className={styles.label}>Postcode:</span>
                                            <span className={styles.value}>{client.postcode || '—'}</span>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => handleSelectClient(client)}
                                        className={styles.selectButton}
                                        disabled={isSelected}
                                    >
                                        {isSelected ? 'Selected' : 'Select'}
                                    </button>
                                </div>
                            )
                        })}
                    </div>
                </div>
            )}
        </div>
    )
}

export default PcpReferralEngagementView
